/* =========================================================================
   hh-shipping-bar.js — free-shipping progress bar reactivity (classic script).

   On load: compute % from the cart total injected on [data-hh-ship]
   (data-cart-total, cents) against data-threshold (cents).
   On cart change: subscribe to Dawn's `cart-update` pubsub event and re-fetch
   /cart.js for the authoritative total (sidesteps Dawn payload-shape drift),
   then re-render.

   IMPORTANT: `subscribe` / `PUB_SUB_EVENTS` are BARE-SCOPE globals from
   constants.js + pubsub.js (NOT window.*). Reference them bare.

   No JS height writes — width is driven by the CSS custom property
   --hh-ship-pct only (CLS-safe). Message text is the only DOM write.
   ========================================================================= */
(function () {
  'use strict';

  // Money formatter from Shopify's money_format string (falls back to
  // Shopify.formatMoney when available). Trailing zero decimals are stripped so
  // the output matches Liquid's `money_without_trailing_zeros` ($25, not $25.00).
  function formatMoney(cents, format) {
    var out;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      out = window.Shopify.formatMoney(cents, format);
    } else {
      var value = (cents / 100).toFixed(2);
      out = (format && format.indexOf('{{') !== -1)
        ? format.replace(/\{\{\s*\w+\s*\}\}/, value)
        : '$' + value;
    }
    // Drop a whole-amount decimal part: "$25.00" → "$25", "1,50 €" stays.
    return out.replace(/([.,])0{2}(\D*)$/, '$2');
  }

  // Two bases (mirrors hh-shipping-bar.liquid): the SHIPPING stage measures the
  // whole-cart total (any $100 ships free); the TOTE stage measures the eligible
  // subtotal only (the collection the BXGY tote discount targets — Shopify can't
  // scope it to all products). cartTotal drives stage 1; eligTotal drives the tote
  // stage so the bar can't promise a tote the discount won't make free.
  // promoTotal = the promo gift's collection-scoped subtotal. While a promo is active
  // the bar leads with it (tote hidden): gift progress first, then free shipping.
  function update(cartTotal, eligTotal, el, promoTotal) {
    var ship = parseInt(el.getAttribute('data-ship-threshold'), 10) || 0;
    var tote = parseInt(el.getAttribute('data-tote-threshold'), 10) || 0;
    var promoOn = promoActive(el);
    var promo = parseInt(el.getAttribute('data-promo-threshold'), 10) || 0;
    var hasTote = el.getAttribute('data-has-tote') === '1' && !promoOn; // tote paused during a promo
    var format = el.getAttribute('data-money-format') || '';
    var pct = 0;
    var msg;

    if (promoOn && (promoTotal || 0) < promo) {
      pct = promo > 0 ? Math.round(((promoTotal || 0) / promo) * 100) : 100;
      msg = (el.getAttribute('data-msg-promo') || '').replace('[amount]', formatMoney(promo - (promoTotal || 0), format));
    } else if (promoOn && cartTotal >= ship) {
      pct = 100;
      msg = el.getAttribute('data-msg-promo-unlocked');
    } else if (hasTote && eligTotal >= tote) {
      pct = 100;
      msg = el.getAttribute('data-msg-tote-unlocked');
    } else if (cartTotal >= ship) {
      if (hasTote) {
        pct = tote > 0 ? Math.round((eligTotal / tote) * 100) : 100;
        msg = (el.getAttribute('data-msg-tote') || '').replace('[amount]', formatMoney(tote - eligTotal, format));
      } else {
        pct = 100;
        msg = el.getAttribute('data-msg-ship-unlocked');
      }
    } else {
      pct = ship > 0 ? Math.round((cartTotal / ship) * 100) : 0;
      msg = (el.getAttribute('data-msg-ship') || '').replace('[amount]', formatMoney(ship - cartTotal, format));
    }

    pct = Math.max(0, Math.min(100, pct));
    el.style.setProperty('--hh-ship-pct', pct + '%');

    var msgEl = el.querySelector('[data-hh-ship-msg]');
    if (msgEl && msg != null) msgEl.textContent = msg;
  }

  // Eligible subtotal (cents) toward a gift's threshold — sums cart lines whose
  // product is in that gift's id list (the discount's collection), excluding the
  // gift's own variant so its price never inflates eligibility. A null id list →
  // whole cart minus the gift (matches the tote's Liquid fallback).
  function parseIdList(raw) {
    raw = (raw || '').trim();
    if (!raw) return null;
    var set = {};
    raw.split(',').forEach(function (s) { var n = parseInt(s, 10); if (n) set[n] = 1; });
    return set;
  }
  function eligibleTotal(cart, ids, excludeVariantId) {
    var items = (cart && cart.items) || [];
    var sum = 0;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.variant_id === excludeVariantId) continue;  // never count the gift itself
      if (ids && !ids[it.product_id]) continue;           // collection-scoped
      sum += it.final_line_price || 0;
    }
    return sum;
  }
  // The promo stage reads the same subtotal the promo gift uses.
  function promoEligibleTotal(cart, el) {
    return eligibleTotal(cart, parseIdList(el.getAttribute('data-promo-eligible-ids')),
                         parseInt(el.getAttribute('data-promo-variant-id'), 10) || 0);
  }
  // The shipping bar's tote stage reads the same subtotal the tote gift uses.
  function toteEligibleTotal(cart, el) {
    return eligibleTotal(cart, parseIdList(el.getAttribute('data-tote-eligible-ids')),
                         parseInt(el.getAttribute('data-tote-variant-id'), 10) || 0);
  }

  // ===========================================================================
  // Gift auto-add (silent gift-with-purchase). Two gifts share this engine:
  //
  //   tote  — the $150 free tote. Mirrors the mockup's App.jsx tote behaviour.
  //           Inert unless data-tote-auto-add='1' (free_tote_auto_add setting).
  //   promo — a limited-time gift (e.g. a feast-day free item). Emitted by the
  //           Liquid only when switched on + configured, and disarmed here once
  //           data-promo-ends-at has passed.
  //
  // Each gift is only FREE via a matching "Buy X get Y" automatic discount, so
  // eligibility is the gift's collection-scoped subtotal (never the gift lines
  // themselves), using post-discount prices — the conservative side: worst case a
  // buyer isn't auto-added; never a buyer charged for a "free" gift near the line.
  //
  // WHY ONE ENGINE, RUN SERIALLY: a mutation here re-renders the drawer but does
  // NOT publish cartUpdate, so a second independent script would never learn the
  // cart changed and would act on a stale cart. Gifts are reconciled one after the
  // other, each against the cart the previous step left behind.
  //
  // The promo gift is also SELF-VERIFYING (mustBeFree): if its line is ever priced
  // above $0 — discount missing, ended, or out-competed by another discount — it
  // is removed; the same unchanged cart isn't retried, but any cart change is.
  //
  // The tote is PAUSED while a promo gift is active (see readGifts): Shopify lets a
  // product earn only one BXGY gift, and the tote would otherwise win on every
  // $150+ body-care cart. Outside a promo the tote behaves exactly as before.
  //
  // Module state survives Dawn's drawer re-renders (the IIFE runs once):
  //   busy                — a reconcile pass is in flight (prevents double-add / loops)
  //   state[name].lastPresent    — was that gift's line in the cart last pass
  //   state[name].scriptRemoving — WE removed it, so its next absence isn't a customer removal
  // ===========================================================================
  var busy = false;
  var state = {};
  function stateFor(name) {
    if (!state[name]) state[name] = { lastPresent: null, scriptRemoving: false };
    return state[name];
  }

  function sessionGet(key) { try { return sessionStorage.getItem(key) === '1'; } catch (e) { return false; } }
  function sessionSet(key) { try { sessionStorage.setItem(key, '1'); } catch (e) {} }
  function sessionClear(key) { try { sessionStorage.removeItem(key); } catch (e) {} }
  function sessionGetValue(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } }
  function sessionSetValue(key, v) { try { sessionStorage.setItem(key, v); } catch (e) {} }

  // Identity of a cart's contents EXCLUDING our gift lines: "variant:qty" pairs, sorted.
  function cartSignature(cart) {
    var items = (cart && cart.items) || [];
    var parts = [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      if (p._free_tote || p['Free gift']) continue;
      parts.push(items[i].variant_id + ':' + items[i].quantity);
    }
    return parts.sort().join(',');
  }

  // Every per-visit gift flag. An EMPTY cart resets them all: the bar (and so this
  // script's reconcile) doesn't exist while the cart is empty, so "dropped below the
  // threshold" would otherwise never be observed and a removed gift could never
  // come back. Starting over from an empty cart is a fresh start.
  var GIFT_FLAG_KEYS = ['hh_tote_dismissed', 'hh_promo_gift_dismissed', 'hh_promo_gift_failed_cart'];
  function resetForEmptyCart() {
    GIFT_FLAG_KEYS.forEach(sessionClear);
    Object.keys(state).forEach(function (name) { state[name] = { lastPresent: false, scriptRemoving: false }; });
  }

  // Promo is "active" from the moment the Liquid emits it until its end time.
  function promoState(el) {
    var promoVariant = parseInt(el.getAttribute('data-promo-variant-id'), 10) || 0;
    var endsAt = Date.parse(el.getAttribute('data-promo-ends-at') || '');
    var timed = !!promoVariant && !isNaN(endsAt);
    return { variantId: promoVariant, active: timed && Date.now() < endsAt, expired: timed && Date.now() >= endsAt };
  }
  function promoActive(el) { return promoState(el).active; }

  function readGifts(el) {
    var toteVariant = parseInt(el.getAttribute('data-tote-variant-id'), 10) || 0;
    var promo = promoState(el);
    var promoVariant = promo.variantId;
    return [
      {
        name: 'tote',
        // PAUSED while a promo gift is active: Shopify lets a product earn only one
        // BXGY gift, so a tote in the cart would claim the body care and leave the
        // promo gift at full price. Our own tote lines are taken out for the duration
        // and come back through the normal add path once the promo ends.
        armed: el.getAttribute('data-tote-auto-add') === '1' && !!toteVariant && !promo.active,
        expired: false,
        paused: promo.active,
        variantId: toteVariant,
        threshold: parseInt(el.getAttribute('data-tote-threshold'), 10) || 0,
        eligibleIds: parseIdList(el.getAttribute('data-tote-eligible-ids')),
        propKey: '_free_tote',
        propValue: '1',
        dismissKey: 'hh_tote_dismissed',
        mustBeFree: false
      },
      {
        name: 'promo',
        armed: promo.active,
        expired: promo.expired,
        paused: false,
        variantId: promoVariant,
        threshold: parseInt(el.getAttribute('data-promo-threshold'), 10) || 0,
        eligibleIds: parseIdList(el.getAttribute('data-promo-eligible-ids')),
        // Visible (no leading underscore) so it prints on the cart line, at checkout, and on the order.
        propKey: 'Free gift',
        propValue: el.getAttribute('data-promo-label') || '',
        dismissKey: 'hh_promo_gift_dismissed',
        failedKey: 'hh_promo_gift_failed_cart',
        mustBeFree: true
      }
    ];
  }

  function findGiftLine(cart, gift) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.variant_id === gift.variantId && it.properties && it.properties[gift.propKey]) return it;
    }
    return null;
  }

  // A paid line of the gift's own variant (the customer added it themselves). The
  // discount's "1 per order" would make THAT line free, so a promo gift line on top
  // would be charged — the promo steps aside instead.
  function hasPaidCopy(cart, gift) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.variant_id === gift.variantId && !(it.properties && it.properties[gift.propKey])) return true;
    }
    return false;
  }

  function renderDrawer(state) {
    var cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.renderContents === 'function') cd.renderContents(state);
  }

  var MUT_HEADERS = { 'Content-Type': 'application/json' };
  function sectionsBody(extra) {
    return Object.assign({ sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }, extra);
  }

  // POST a cart mutation, re-render the drawer from its sections, then resolve with
  // the fresh full cart (the add.js response is the ITEM, not the cart).
  function mutate(url, extra, tag) {
    return fetch(url, { method: 'POST', headers: MUT_HEADERS, body: JSON.stringify(sectionsBody(extra)) })
      .then(function (r) { if (!r.ok) throw new Error(tag + ' ' + r.status); return r.json(); })
      .then(function (res) { renderDrawer(res); return fetch('/cart.js'); })
      .then(function (r) { return r.json(); });
  }
  function removeLine(gift, key) {
    stateFor(gift.name).scriptRemoving = true;
    return mutate('/cart/change.js', { id: key, quantity: 0 }, 'remove')
      .then(function (c) { stateFor(gift.name).lastPresent = false; return c; });
  }

  // Promo only: the line must be exactly one unit at $0, or it comes out. The cart it
  // failed on is remembered (its non-gift lines), so the SAME cart isn't retried in a
  // loop — but any change to the cart tries again. (Previously one failure switched
  // the promo off for the whole visit, which stranded customers whose cart later
  // qualified.)
  function enforceFree(cart, gift) {
    var line = findGiftLine(cart, gift);
    if (!line) return Promise.resolve(cart);
    if (line.quantity > 1) {
      return mutate('/cart/change.js', { id: line.key, quantity: 1 }, 'trim')
        .then(function (c) { return enforceFree(c, gift); });
    }
    if ((line.final_line_price || 0) > 0) {
      console.warn('[hh-gift] promo gift is not discounted to $0 for this cart — removing it; will retry when the cart changes');
      sessionSetValue(gift.failedKey, cartSignature(cart));
      return removeLine(gift, line.key);
    }
    return Promise.resolve(cart);
  }

  // Customer removed the gift (was present, now gone, still eligible, not by us) →
  // remember it for the session. Runs even while a pass is in flight, as the
  // original tote code did, so a removal mid-update is never mistaken for ours.
  // Returns whether the cart is currently eligible for this gift.
  function noteDismissal(cart, gift) {
    var st = stateFor(gift.name);
    var eligible = eligibleTotal(cart, gift.eligibleIds, gift.variantId) >= gift.threshold;
    if (st.lastPresent === true && !findGiftLine(cart, gift) && eligible && !st.scriptRemoving) sessionSet(gift.dismissKey);
    st.scriptRemoving = false;
    return eligible;
  }

  // Returns a promise of the cart as it stands after this gift is reconciled.
  function reconcileGift(cart, gift) {
    var st = stateFor(gift.name);
    var line = findGiftLine(cart, gift);
    var present = !!line;

    if (!gift.variantId) return Promise.resolve(cart);

    if (!gift.armed) {
      st.lastPresent = present;
      // An ended promo must not leave a now-priced gift line behind, and a paused
      // tote must not sit in the cart claiming products the promo gift needs.
      if ((gift.expired || gift.paused) && present) return removeLine(gift, line.key);
      return Promise.resolve(cart);
    }

    if (gift.mustBeFree) {
      var failedOn = sessionGetValue(gift.failedKey);
      if (failedOn && failedOn === cartSignature(cart)) {
        st.lastPresent = present;
        return present ? removeLine(gift, line.key) : Promise.resolve(cart);
      }
      if (failedOn) sessionClear(gift.failedKey); // cart changed since the failure → try again
    }

    var eligible = noteDismissal(cart, gift);

    var dismissed = sessionGet(gift.dismissKey);
    // Dropping below the threshold clears the dismissal, so re-crossing re-adds.
    if (!eligible && dismissed) { sessionClear(gift.dismissKey); dismissed = false; }

    if (gift.mustBeFree && present && hasPaidCopy(cart, gift)) return removeLine(gift, line.key);

    if (eligible && !present && !dismissed && !(gift.mustBeFree && hasPaidCopy(cart, gift))) {
      var props = {}; props[gift.propKey] = gift.propValue;
      return mutate('/cart/add.js', { items: [{ id: gift.variantId, quantity: 1, properties: props }] }, 'add')
        .then(function (c) { st.lastPresent = true; return gift.mustBeFree ? enforceFree(c, gift) : c; });
    }
    if (!eligible && present) return removeLine(gift, line.key);

    st.lastPresent = present;
    return gift.mustBeFree ? enforceFree(cart, gift) : Promise.resolve(cart);
  }

  // A cart event that lands while a pass is in flight is NOT dropped: it is noted
  // (dismissal detection) and a fresh pass runs once the current one finishes, so
  // e.g. an add during the page-load pass still gets its gift immediately.
  var rerun = false;
  function reconcileGifts(cart, el) {
    var gifts = readGifts(el);
    if (busy) {
      gifts.forEach(function (g) {
        if (g.armed && g.variantId) noteDismissal(cart, g);
        stateFor(g.name).lastPresent = !!findGiftLine(cart, g);
      });
      rerun = true;
      return;
    }
    busy = true;
    gifts.reduce(function (p, g) {
      return p.then(function (c) { return reconcileGift(c, g); });
    }, Promise.resolve(cart))
      .catch(function (e) { console.warn('[hh-gift] cart update failed:', e && e.message); })
      .then(function () {
        busy = false;
        if (!rerun) return;
        rerun = false;
        var fresh = document.querySelector('[data-hh-ship]');
        if (!fresh) return;
        return fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (c) { refresh(c, fresh); });
      })
      .catch(function () { /* leave cart untouched */ });
  }

  function refresh(c, el) {
    update(c.total_price, toteEligibleTotal(c, el), el, promoEligibleTotal(c, el));
    reconcileGifts(c, el);
  }

  function giftsNeedCart(el) {
    return readGifts(el).some(function (g) { return g.variantId && (g.armed || g.expired || g.paused); });
  }

  function init() {
    // Initial render from the server-injected total (already painted by Liquid,
    // but re-run to normalize the message + fill in one place). Only when the
    // bar is present at load — on an empty-cart page it isn't rendered yet.
    var el = document.querySelector('[data-hh-ship]');
    if (el) {
      // Instant paint from the server-injected totals (whole-cart + eligible subtotal),
      // so the tote stage is correct before any fetch.
      update(parseInt(el.getAttribute('data-cart-total'), 10) || 0,
             parseInt(el.getAttribute('data-elig-subtotal'), 10) || 0, el,
             parseInt(el.getAttribute('data-promo-subtotal'), 10) || 0);
      // If any gift is armed (or a promo just ended), an already-loaded cart needs
      // reconciling. Needs the full cart (line items), so fetch once — only then.
      if (giftsNeedCart(el)) {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (c) { refresh(c, el); })
          .catch(function () { /* leave cart untouched */ });
      }
    }

    // Subscribe UNCONDITIONALLY — never gate on the initial `el`. An empty-cart
    // page has no bar at load, so capturing `el` once would mean we never react
    // to the first add. Re-query [data-hh-ship] INSIDE the callback every time:
    // Dawn re-renders the whole .drawer__inner on cart change, so any node we
    // closed over would be detached and our updates invisible.
    //
    // Dawn re-renders this bar server-side too, so this JS is a fast-path /
    // safety-net — but it now actually works after a re-render and after the
    // first add on an empty cart.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        var fresh = document.querySelector('[data-hh-ship]');
        if (!fresh) { resetForEmptyCart(); return; } // empty cart → bar not in the DOM; fresh start
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (c) { refresh(c, fresh); })
          .catch(function () { /* leave last-known state */ });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
